import { useState } from 'react'
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Tabs,
  Text,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  Maximize2,
  Minimize2,
} from 'lucide-react'

import { hasPermissions } from '@common/utils/validation/authValidators'
import { FIRMWARE_OPTIONS } from '@components/main/DeviceManagement/DeviceInformationManager/constants'
import { DeviceCommandsTab } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/DeviceCommandsTab'
import { DeviceNetworkTab } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/DeviceNetworkTab'
import {
  useDeviceQueryMutationState,
  useTestDeviceFirmwareUpdate,
  useTestDeviceRefresh,
  useTestDeviceReset,
} from '@data/device/device'
import { type TestDevice } from '@data/device/model/device'
import { useAuthStore } from '@store/auth.store'

import { TestDeviceStatusTab } from './TestDeviceStatusTab'
import { TestDeviceValvesTab } from './TestDeviceValvesTab'

export interface TestDeviceModalProps {
  opened: boolean
  device: TestDevice
  onClose: () => void
}

export const TestDeviceModal = ({
  opened,
  device,
  onClose,
}: TestDeviceModalProps) => {
  const permissions = useAuthStore((state) => state.permissions)
  const {
    deviceSendingMessage,
    deviceUpdating,
  } = useDeviceQueryMutationState()
  const {
    mutate: refreshDevice,
    isPending: refreshing,
  } = useTestDeviceRefresh()
  const { mutate: updateFirmware } = useTestDeviceFirmwareUpdate()
  const { mutate: resetDevice } = useTestDeviceReset()

  // The same gate Device Management puts on its commands tab, minus the device-admin scope,
  // which belongs to provisioned devices. Without either command scope the tab would render
  // empty, so it is hidden and the modal opens on Network Info instead.
  const canSendCommands =
    device.deviceVendor === 'SMARTFLOW' &&
    (hasPermissions(permissions, ['DEVICE:FIRMWARE:UPDATE']) ||
      hasPermissions(permissions, ['DEVICE:RESET']))

  const [activeTab, setActiveTab] = useState<string>(canSendCommands ? 'commands' : 'network')
  const [fullScreen, setFullScreen] = useState(false)
  const isMobile = useMediaQuery('(max-width: 47.99em)')
  const isFullScreen = isMobile || fullScreen

  // Shown on the same condition as Device Management's Valves tab. The panels inside gate
  // themselves on DEVICE:VALVE:COMM, as they do there.
  const hasValves = device.deviceVendor === 'SMARTFLOW'

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      fullScreen={isFullScreen}
      centered={!isFullScreen}
      size='80%'
      radius={isFullScreen ? 0 : 'md'}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Group
            justify='space-between'
            style={{ width: '100%' }}
          >
            <Modal.Title>
              <Text fw={600}>{device.deviceId}</Text>
            </Modal.Title>

            <Group gap='xs'>
              <ActionIcon
                variant='subtle'
                c='dimmed'
                aria-label={fullScreen ? 'Exit full screen' : 'Open full screen'}
                visibleFrom='sm'
                onClick={() => setFullScreen((value) => !value)}
              >
                {fullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </ActionIcon>
              <Modal.CloseButton onClick={onClose} />
            </Group>
          </Group>
        </Modal.Header>
        <Modal.Body>
          <Stack gap='md'>
            <Group justify='space-between'>
              <Text
                size='sm'
                c='dimmed'
              >
                Test device
              </Text>

              <Button
                variant='light'
                onClick={() => refreshDevice(device.deviceId)}
                loading={refreshing}
              >
                Refresh
              </Button>
            </Group>

            <Tabs
              value={activeTab}
              onChange={(value) => setActiveTab(value || 'network')}
            >
              <Tabs.List>
                {canSendCommands && (
                  <Tabs.Tab value='commands'>
                    Device Commands
                  </Tabs.Tab>
                )}

                {hasValves && (
                  <Tabs.Tab value='valves'>
                    Valves
                  </Tabs.Tab>
                )}

                <Tabs.Tab value='network'>
                  Network Info
                </Tabs.Tab>

                <Tabs.Tab value='status'>
                  Status
                </Tabs.Tab>
              </Tabs.List>

              {canSendCommands && (
                <Tabs.Panel
                  value='commands'
                  pt='md'
                >
                  <DeviceCommandsTab
                    device={device}
                    deviceSendingMessage={deviceSendingMessage}
                    firmwareOptions={FIRMWARE_OPTIONS}
                    permissions={permissions}
                    onFirmwareUpdate={(binFileName) => updateFirmware({
                      deviceId: device.deviceId,
                      binFileName,
                    })}
                    onReset={() => resetDevice(device.deviceId)}
                  />
                </Tabs.Panel>
              )}

              {hasValves && (
                <Tabs.Panel
                  value='valves'
                  pt='md'
                >
                  <TestDeviceValvesTab
                    device={device}
                    permissions={permissions}
                    deviceUpdating={deviceUpdating}
                  />
                </Tabs.Panel>
              )}

              <Tabs.Panel
                value='network'
                pt='md'
              >
                <DeviceNetworkTab
                  device={device}
                  showSimDetails
                />
              </Tabs.Panel>

              <Tabs.Panel
                value='status'
                pt='md'
              >
                <TestDeviceStatusTab device={device} />
              </Tabs.Panel>
            </Tabs>

            <Group justify='flex-end'>
              <Button
                variant='default'
                onClick={onClose}
              >
                Close
              </Button>
            </Group>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
}
